import { HttpClient } from '@angular/common/http';
import { inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, finalize, Observable } from 'rxjs';
import { EnvironmentToken } from '../../app.config';
import { Route, RouteSection } from '../models/route.model';

export class RouteService {
  #http = inject(HttpClient);
  #apiUrl = inject(EnvironmentToken).apiUrl;
  #isLoading = signal<Set<unknown>>(new Set());

  #routes = new BehaviorSubject<Route[]>([]);

  public routes$ = this.#routes.asObservable();
  public routes = toSignal(this.routes$, {
    initialValue: [],
  });

  getRoutes() {
    this.#execute(
      this.#http.get<Route[]>(`${this.#apiUrl}/api/routes`),
    ).subscribe({
      next: (routes: any) => {
        this.#routes.next(routes.data ?? routes);
      },
      error: (e: any) => console.log(e),
    });
  }

  getRouteSections(routeId: number) {
    return this.#execute(
      this.#http.get<RouteSection[]>(
        `${this.#apiUrl}/api/routes/${routeId}/sections`,
      ),
    );
  }

  createRouteSection(routeId: number, data: Partial<RouteSection>) {
    return this.#execute(
      this.#http.post<RouteSection>(
        `${this.#apiUrl}/api/routes/${routeId}/sections`,
        data,
      ),
    );
  }

  updateRouteSection(
    routeId: number,
    sectionId: string,
    data: Partial<RouteSection>,
  ) {
    return this.#execute(
      this.#http.put<RouteSection>(
        `${this.#apiUrl}/api/routes/${routeId}/sections/${sectionId}`,
        data,
      ),
    );
  }

  deleteRouteSection(routeId: number, sectionId: string) {
    return this.#execute(
      this.#http.delete(
        `${this.#apiUrl}/api/routes/${routeId}/sections/${sectionId}`,
      ),
    );
  }

  #execute<T = unknown>(apiCall: Observable<T>) {
    this.#isLoading.update((x) => {
      x.add(apiCall);
      return new Set(x.values());
    });
    return apiCall.pipe(
      finalize(() =>
        this.#isLoading.update((x) => {
          x.delete(apiCall);
          return new Set(x.values());
        }),
      ),
    );
  }
}
